import { api } from '../lib/api';
import type { CategoryCollection, EntitySummary, EntityTag, ExplorerNode } from '../types/entities';
import type { WorkspaceMember, WorkspaceRole, WorkspaceSettings, WorkspaceSummary } from '../types/workspace';

type WorkspaceCatalogStore = {
  workspace: WorkspaceSummary | null;
  availableWorkspaces: WorkspaceSummary[];
  archivedWorkspaces: WorkspaceSummary[];
  entities: EntitySummary[];
  activeEntity: { id: string } | null;
  activeEntityTags: EntityTag[];
  tags: EntityTag[];
  explorerNodes: ExplorerNode[];
  categoryCollections: CategoryCollection[];
  loadWorkspaceList(includeArchived?: boolean): Promise<WorkspaceSummary[]>;
  switchWorkspace(workspaceId: string): Promise<void>;
  loadTags(): Promise<void>;
  loadEntities(): Promise<void>;
};

export const workspaceCatalogActions = {
  async createWorkspace(this: WorkspaceCatalogStore, name: string) {
    const response = await api<{ workspace: WorkspaceSummary }>('/api/v1/workspaces', {
      method: 'POST', body: JSON.stringify({ name })
    });
    this.availableWorkspaces = [...this.availableWorkspaces, response.workspace];
    return response.workspace;
  },
  async updateWorkspace(this: WorkspaceCatalogStore, workspaceId: string, input: {
    name: string; defaultLanguage: string; technicalTimezone: string; settings: WorkspaceSettings
  }) {
    const response = await api<{ workspace: WorkspaceSummary }>(`/api/v1/workspaces/${workspaceId}`, { method: 'PATCH', body: JSON.stringify(input) });
    this.availableWorkspaces = this.availableWorkspaces.map((item) => item.id === workspaceId ? response.workspace : item);
    this.archivedWorkspaces = this.archivedWorkspaces.map((item) => item.id === workspaceId ? response.workspace : item);
    if (this.workspace?.id === workspaceId) this.workspace = response.workspace;
    return response.workspace;
  },
  async archiveWorkspace(this: WorkspaceCatalogStore, workspaceId: string) {
    await api(`/api/v1/workspaces/${workspaceId}/archive`, { method: 'POST' });
    await this.loadWorkspaceList(true);
    if (this.workspace?.id === workspaceId && this.availableWorkspaces[0]) await this.switchWorkspace(this.availableWorkspaces[0].id);
  },
  async restoreWorkspace(this: WorkspaceCatalogStore, workspaceId: string) {
    await api(`/api/v1/workspaces/${workspaceId}/restore`, { method: 'POST' });
    await this.loadWorkspaceList(true);
  },
  async deleteWorkspace(this: WorkspaceCatalogStore, workspaceId: string, confirmation: string) {
    await api(`/api/v1/workspaces/${workspaceId}`, { method: 'DELETE', body: JSON.stringify({ confirmation }) });
    await this.loadWorkspaceList(true);
    if (this.workspace?.id === workspaceId && this.availableWorkspaces[0]) await this.switchWorkspace(this.availableWorkspaces[0].id);
  },
  async loadWorkspaceMembers(this: WorkspaceCatalogStore, workspaceId: string) {
    return (await api<{ items: WorkspaceMember[] }>(`/api/v1/workspaces/${workspaceId}/members`)).items;
  },
  async addWorkspaceMember(this: WorkspaceCatalogStore, workspaceId: string, username: string, role: WorkspaceRole) {
    return (await api<{ member: WorkspaceMember }>(`/api/v1/workspaces/${workspaceId}/members`, { method: 'POST', body: JSON.stringify({ username, role }) })).member;
  },
  async updateWorkspaceMember(this: WorkspaceCatalogStore, workspaceId: string, userId: string, role: WorkspaceRole) {
    return (await api<{ member: WorkspaceMember }>(`/api/v1/workspaces/${workspaceId}/members/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) })).member;
  },
  async removeWorkspaceMember(this: WorkspaceCatalogStore, workspaceId: string, userId: string) {
    await api(`/api/v1/workspaces/${workspaceId}/members/${userId}`, { method: 'DELETE' });
  },
  async loadTags(this: WorkspaceCatalogStore) {
    const workspace = this.workspace; if (!workspace) return;
    const response = await api<{ items: EntityTag[] }>(`/api/v1/workspaces/${workspace.id}/tags`);
    if (this.workspace === workspace) this.tags = response.items;
  },
  async loadEntityTags(this: WorkspaceCatalogStore, entityId?: string) {
    const id = entityId ?? this.activeEntity?.id;
    if (!id) { this.activeEntityTags = []; return; }
    const entity = this.activeEntity;
    const response = await api<{ items: EntityTag[] }>(`/api/v1/entities/${id}/tags`);
    if (this.activeEntity === entity && entity?.id === id) this.activeEntityTags = response.items;
  },
  async loadEntities(this: WorkspaceCatalogStore) {
    if (!this.workspace) return;
    const workspace = this.workspace;
    const items: EntitySummary[] = [];
    let cursor: string | null = null;
    do {
      const suffix: string = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
      const response: { items: EntitySummary[]; nextCursor: string | null } = await api(`/api/v1/workspaces/${workspace.id}/entities?status=all&limit=200${suffix}`);
      items.push(...response.items);
      cursor = response.nextCursor;
    } while (cursor && this.workspace === workspace);
    if (this.workspace === workspace) this.entities = items;
  },
  async setEntityTags(this: WorkspaceCatalogStore, names: string[]) {
    if (!this.activeEntity) return;
    const response = await api<{ items: EntityTag[] }>(`/api/v1/entities/${this.activeEntity.id}/tags`, { method: 'PUT', body: JSON.stringify({ names }) });
    this.activeEntityTags = response.items;
    await this.loadTags();
  },
  async loadExplorer(this: WorkspaceCatalogStore) {
    const workspace = this.workspace; if (!workspace) return;
    const response = await api<{ items: ExplorerNode[] }>(`/api/v1/workspaces/${workspace.id}/explorer-nodes`);
    if (this.workspace === workspace) this.explorerNodes = response.items;
  },
  async loadCategoryCollections(this: WorkspaceCatalogStore) {
    const workspace = this.workspace; if (!workspace) return;
    const response = await api<{ items: CategoryCollection[] }>(`/api/v1/workspaces/${workspace.id}/category-collections`);
    if (this.workspace === workspace) this.categoryCollections = response.items;
  }
};
